import { whyFor, fixFor } from './shapeReport';
import { leadMagnetType } from '../../v2/lib/leadMagnetTypes';

// The security report, as a printed document.
//
// Replaces a dark, gradient-heavy web page photographed onto A4. This is laid out as
// paper: a warm ground, one accent, black text, and severity carried by colour only where
// colour means something. Each page is composed at exactly A4 proportions and placed edge
// to edge, so the rule at the head of the page and the footer at its foot are part of the
// design rather than something jsPDF draws afterwards.
//
// ── Colour ────────────────────────────────────────────────────────────────────────────
// Every colour is a token in PALETTE, and `options.palette` overrides any of them. Nothing
// below writes a literal colour, which is what will make the planned per-tenant colour
// controls a UI change rather than a rewrite of this file: whatever collects the choice
// hands it in as a partial palette.
//
// The severity ramp is deliberately not the accent. Navy is identity — rules, section
// numbers, the score — and red/amber/green only ever mean severity, so a reader never has
// to ask whether a colour is decorative.
export const PALETTE = {
  ground: '#FBF9F4',     // the paper
  surface: '#FFFFFF',    // cards sitting on it
  ink: '#14181F',        // headings
  body: '#2E333B',       // running text
  muted: '#6E7480',      // labels, captions
  rule: '#DDD6C8',       // hairlines
  ruleStrong: '#1B3A5C', // the navy rule under a table head
  accent: '#1B3A5C',     // navy
  accentSoft: '#E8EDF3',
  critical: '#7B1E1E',
  high: '#C2410C',
  medium: '#D9A520',
  low: '#9FB3CD',
  pass: '#2F6B3F',       // a control confirmed in place
};

// Serif for display, sans for reading, mono for the small uppercase labels. All three are
// web-safe stacks: html2canvas paints whatever the browser has, and a webfont that has not
// finished loading silently falls back mid-render, which shifts the layout it measured.
const FONT_DISPLAY = "Georgia, 'Iowan Old Style', 'Times New Roman', serif";
const FONT_BODY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";
const FONT_MONO = "ui-monospace, Menlo, Consolas, 'Courier New', monospace";

// A4 at 96dpi. The canvas is rendered at this size and scaled to the page, so these are
// the only geometry numbers in the file.
const PAGE_W = 794;
const PAGE_H = 1123;
const MARGIN = 74;
const BODY_H = PAGE_H - 210; // page height less the header and footer bands

const esc = (s = '') => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Scanner detail text is written for the web: emoji section markers, bullet runs and
// headings that make no sense inside a printed paragraph.
const clean = (s = '', limit = 260) => {
  const flat = String(s ?? '')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  // whyFor/fixFor lift a fragment out of the middle of the scanner's detail text, which
  // regularly starts mid-sentence in lower case. In a table cell that reads as a typo.
  const cased = flat.charAt(0).toUpperCase() + flat.slice(1);
  return cased.length > limit ? `${cased.slice(0, limit - 1).trimEnd()}…` : cased;
};

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
const sevRank = (s) => {
  const i = SEV_ORDER.indexOf(String(s || 'info').toLowerCase());
  return i === -1 ? SEV_ORDER.length : i;
};

const loadPdfLibraries = async () => {
  const load = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const el = document.createElement('script');
    el.src = src; el.onload = resolve; el.onerror = reject;
    document.head.appendChild(el);
  });
  await load('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
  await load('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
  window.jspdf = window.jspdf || window.jsPDF;
};

/**
 * Render a scan result as a print-styled PDF.
 *
 * @param {object} results   performDomainScan output
 * @param {string} domain    the scanned target, as typed
 * @param {object} options
 * @param {object} options.palette        partial PALETTE override
 * @param {string} options.prospectCompany company name; titles the report when present
 * @param {string} options.prospectName    named recipient, shown under the title
 * @param {boolean} options.saveFile       save to disk (default) or resolve a Blob
 * @param {string} options.fileName
 * @param {string} options.reportType      the lead magnet this scan was run as; decides
 *                                         what the document calls itself
 * @param {function} options.onProgress    (percent, message)
 */
export async function generateEditorialPDF(results, domain, options = {}) {
  const {
    palette: paletteOverride = {},
    prospectCompany = '',
    prospectName = '',
    saveFile = true,
    fileName = null,
    reportType = null,
    onProgress = null,
  } = options;

  // A focused lead magnet runs one module, so its report already contains nothing but that
  // module's findings — but headed "Security Posture Assessment" it reads as a full
  // assessment with most of itself missing. Unknown or absent types keep the full-scan
  // wording, which is what a scan with no widget behind it is.
  const magnet = leadMagnetType(reportType);

  const P = { ...PALETTE, ...paletteOverride };
  const progress = (pct, msg) => { if (typeof onProgress === 'function') onProgress(pct, msg); };

  progress(5, 'Preparing report…');
  await loadPdfLibraries();

  // ── The numbers, counted once ───────────────────────────────────────────────────────
  const displayDomain = String(domain ?? '')
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/\/.*$/, '')
    .replace(/\.+$/, '') || String(domain ?? '');

  const categories = (results?.categories || []).filter((c) => (c.findings || []).length);
  const allFindings = categories.flatMap((c) => (c.findings || []).map((f) => ({ ...f, category: c.name })));
  // Issues and passes are counted apart, as the on-screen report does. A passing check
  // carries a severity too, so counting every check put "4 findings · 4 Low" on a scan
  // where all four checks passed — while the screen beside it said 0 issues.
  const isPass = (f) => f.status === 'pass';
  const issues = allFindings.filter((f) => !isPass(f));
  const passes = allFindings.filter(isPass);
  const count = (sev) => issues.filter((f) => f.severity === sev).length;
  const criticalCount = count('critical');
  const highCount = count('high');
  const mediumCount = count('medium');
  // Info and low read as one band to a reader: neither is something to act on today.
  const lowCount = issues.filter((f) => f.severity === 'low' || f.severity === 'info').length;
  const total = allFindings.length;       // checks run
  const issueCount = issues.length;
  const passed = passes.length;
  const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const score = Number.isFinite(results?.riskScore) ? Math.round(results.riskScore) : 0;

  const band = criticalCount ? 'critical' : highCount ? 'high' : mediumCount ? 'medium' : 'low';
  const bandLabel = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }[band];
  const bandColor = P[band];

  const highFindings = issues.filter((f) => f.severity === 'critical' || f.severity === 'high')
    .sort((a, b) => sevRank(a.severity) - sevRank(b.severity));
  const mediumFindings = issues.filter((f) => f.severity === 'medium');
  const lowFindings = issues.filter((f) => f.severity === 'low' || f.severity === 'info');

  // Section numbers, assigned only to sections that will be printed, so a report with no
  // high findings reads 01, 02, 03 rather than jumping from 02 to 05.
  const sectionNo = {};
  let lastSection = 2;
  [['high', highFindings], ['medium', mediumFindings], ['low', lowFindings], ['pass', passes]]
    .forEach(([key, list]) => { if (list.length) sectionNo[key] = String(++lastSection).padStart(2, '0'); });

  const title = prospectCompany || displayDomain;
  const scanDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  // ── Shared pieces ───────────────────────────────────────────────────────────────────
  const eyebrow = (text, color = P.accent) => `
    <div style="font-family:${FONT_MONO};font-size:10.5px;letter-spacing:.14em;text-transform:uppercase;color:${color};">
      ${esc(text)}
    </div>`;

  const heading = (text, size = 34) => `
    <h1 style="margin:10px 0 0;font-family:${FONT_DISPLAY};font-size:${size}px;line-height:1.14;
               font-weight:700;color:${P.ink};letter-spacing:-.01em;">${esc(text)}</h1>`;

  const lede = (text) => `
    <p style="margin:12px 0 0;font-family:${FONT_BODY};font-size:14px;line-height:1.6;color:${P.body};">${text}</p>`;

  // `label` is separate from `sev` deliberately: the colour has to key off the severity
  // alone. Deriving it from the text meant a pill reading "High risk" looked up a palette
  // entry of that name, found none, and fell back to the low-severity pale blue — the
  // cover's risk pill was painted as if the domain were fine.
  const sevPill = (sev, label) => {
    const s = String(sev || 'info').toLowerCase();
    const bg = P[s] || P.low;
    // Amber and pale blue are too light to carry white text at this size.
    const fg = s === 'medium' || s === 'low' || s === 'info' ? P.ink : '#fff';
    return `<span style="display:inline-block;padding:3px 9px;border-radius:2px;background:${bg};color:${fg};
                  font-family:${FONT_MONO};font-size:10px;letter-spacing:.09em;text-transform:uppercase;">${esc(label || s)}</span>`;
  };

  const outlinePill = (text) => `
    <span style="display:inline-block;padding:3px 9px;border-radius:2px;border:1px solid ${P.rule};
                 color:${P.body};font-family:${FONT_MONO};font-size:10px;letter-spacing:.06em;">${esc(text)}</span>`;

  // Statistic with a coloured rule over it — the reference's counter row.
  const stat = (value, label, ruleColor) => `
    <div style="flex:1;min-width:0;">
      <div style="height:3px;background:${ruleColor};margin-bottom:10px;"></div>
      <div style="font-family:${FONT_DISPLAY};font-size:30px;line-height:1;color:${P.ink};">${esc(value)}</div>
      <div style="margin-top:6px;font-family:${FONT_BODY};font-size:11.5px;color:${P.muted};">${esc(label)}</div>
    </div>`;

  // ── Page shell ──────────────────────────────────────────────────────────────────────
  // Every page carries the same head rule, running head and footer, so a page pulled out
  // of the middle still says what it is and who it is for.
  const page = (inner, { runningHead, pageNo, pageCount }) => `
    <div style="position:relative;width:${PAGE_W}px;height:${PAGE_H}px;background:${P.ground};
                box-sizing:border-box;overflow:hidden;font-family:${FONT_BODY};">
      <div style="height:10px;background:${P.accent};"></div>
      <div style="padding:0 ${MARGIN}px;">
        <div style="display:flex;justify-content:space-between;align-items:baseline;
                    padding:34px 0 0;font-family:${FONT_MONO};font-size:10.5px;
                    letter-spacing:.12em;text-transform:uppercase;color:${P.muted};">
          <span>${esc(runningHead)}</span><span>Confidential</span>
        </div>
        <div style="height:${BODY_H}px;padding-top:30px;box-sizing:border-box;">${inner}</div>
      </div>
      <div style="position:absolute;left:${MARGIN}px;right:${MARGIN}px;bottom:34px;">
        <div style="height:1px;background:${P.rule};margin-bottom:12px;"></div>
        <div style="display:flex;justify-content:space-between;font-family:${FONT_MONO};
                    font-size:10px;color:${P.muted};letter-spacing:.06em;">
          <span>Confidential Security Report · Generated by Pipsqueak NetScan</span>
          <span>Page ${pageNo} of ${pageCount}</span>
        </div>
      </div>
    </div>`;

  // ── 1. Cover ────────────────────────────────────────────────────────────────────────
  const meta = (label, value) => `
    <div style="flex:1;min-width:0;">
      <div style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:${P.muted};">${esc(label)}</div>
      <div style="margin-top:6px;font-family:${FONT_BODY};font-size:14px;font-weight:600;color:${P.ink};word-break:break-word;">${esc(value)}</div>
    </div>`;

  // The cover is a flex column so the counter row sits on the footer rule rather than
  // wherever the content above happens to end — the block above it varies with the length
  // of the company name and the risk sentence.
  const coverPage = `
    <div style="height:100%;display:flex;flex-direction:column;padding-top:86px;box-sizing:border-box;">
      ${prospectCompany ? eyebrow(`Prepared for ${prospectCompany}`) : eyebrow('External security assessment')}
      ${heading(magnet.reportTitle, magnet.reportTitle.length > 34 ? 38 : 46)}
      <p style="margin:20px 0 0;max-width:470px;font-family:${FONT_BODY};font-size:15px;line-height:1.6;color:${P.body};">
        ${esc(magnet.reportLede)} Written for business decision makers.
      </p>
      ${prospectName ? `<p style="margin:10px 0 0;font-family:${FONT_BODY};font-size:13px;color:${P.muted};">Prepared for ${esc(prospectName)}</p>` : ''}

      <div style="height:1px;background:${P.ink};margin:54px 0 22px;"></div>
      <div style="display:flex;gap:20px;">
        ${meta('Organization', prospectCompany || displayDomain)}
        ${meta('Target', displayDomain)}
        ${meta('Scan date', scanDate)}
        ${meta('Issues found', `${issueCount} of ${plural(total, 'check')}`)}
      </div>

      <div style="margin-top:34px;border:1px solid ${P.rule};background:${P.surface};padding:26px 28px;display:flex;gap:30px;">
        <div style="width:190px;flex:none;">
          <div style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:${P.muted};">Overall security score</div>
          <div style="margin-top:10px;font-family:${FONT_DISPLAY};font-size:62px;line-height:1;color:${P.accent};">
            ${score}<span style="font-size:22px;color:${P.muted};">/100</span>
          </div>
          <div style="margin-top:12px;">${sevPill(band, `${bandLabel} risk`)}</div>
        </div>
        <div style="flex:1;min-width:0;">
          <p style="margin:0;font-family:${FONT_BODY};font-size:13.5px;line-height:1.6;color:${P.body};">
            ${esc(displayDomain)} exhibits a <strong>${esc(bandLabel)}</strong> risk profile.
            ${criticalCount
              ? `${criticalCount} critical ${criticalCount === 1 ? 'issue was' : 'issues were'} detected across the ${plural(total, 'check')} in this assessment.`
              : issueCount
                ? `No critical vulnerabilities were detected across the ${plural(total, 'check')} in this assessment.`
                : `All ${plural(total, 'check')} in this assessment passed.`}
          </p>
          <div style="margin-top:18px;position:relative;height:9px;background:${P.accentSoft};">
            <div style="position:absolute;left:0;top:0;bottom:0;width:${Math.max(2, Math.min(100, score))}%;background:${P.accent};"></div>
            <div style="position:absolute;left:${Math.max(0, Math.min(99, score))}%;top:-4px;width:2px;height:17px;background:${P.ink};"></div>
          </div>
          <div style="margin-top:8px;display:flex;justify-content:space-between;font-family:${FONT_MONO};font-size:10px;color:${P.muted};">
            <span>0 · Critical risk</span><span>100 · No issues found</span>
          </div>
          <p style="margin:12px 0 0;font-family:${FONT_BODY};font-size:12px;color:${P.muted};">
            Higher is better. The marker shows ${esc(displayDomain)} at ${score}.
          </p>
        </div>
      </div>

      <div style="margin-top:auto;padding-top:30px;display:flex;gap:20px;">
        ${stat(criticalCount, 'Critical', P.critical)}
        ${stat(highCount, 'High', P.high)}
        ${stat(mediumCount, 'Medium', P.medium)}
        ${stat(lowCount, 'Low', P.low)}
      </div>
    </div>`;

  // ── 2. Executive summary ────────────────────────────────────────────────────────────
  const segment = (n, color, label, fg) => {
    if (!n) return '';
    const pct = (n / Math.max(1, issueCount)) * 100;
    return `<div style="width:${pct}%;background:${color};color:${fg};font-family:${FONT_MONO};font-size:10px;
                 line-height:24px;padding-left:8px;box-sizing:border-box;overflow:hidden;white-space:nowrap;">
              ${n} ${esc(label)}
            </div>`;
  };

  const legendDot = (color, label, n) => `
    <span style="display:inline-flex;align-items:center;gap:6px;font-family:${FONT_BODY};font-size:11.5px;color:${P.body};">
      <span style="width:9px;height:9px;background:${color};display:inline-block;"></span>${esc(label)} · ${n}
    </span>`;

  // Written from what the scan actually returned rather than a fixed list, so a report
  // with no high findings does not carry a takeaway about high findings.
  const takeaways = [];
  if (!criticalCount) {
    takeaways.push(['Proactive security posture',
      'No critical vulnerabilities were detected. Continue maintaining the controls already in place.']);
  } else {
    takeaways.push(['Critical exposure to close now',
      `${criticalCount} finding${criticalCount === 1 ? '' : 's'} rated critical. These are exploitable today and should be treated as incident work.`]);
  }
  if (highCount) {
    takeaways.push([`${highCount === 1 ? 'One high-severity gap' : `${highCount} high-severity gaps`}`,
      clean(highFindings.slice(0, 2).map((f) => f.title).join('. '), 150) || 'Address these before the medium-severity items.']);
  }
  const byCategory = categories.map((c) => ({
    name: c.name,
    scored: (c.findings || []).filter((f) => f.status !== 'pass' && f.severity !== 'info').length,
  })).sort((a, b) => b.scored - a.scored);
  if (byCategory[0]?.scored > 1) {
    takeaways.push(['One place to fix most of it',
      `${byCategory[0].scored} of the scored findings sit in ${byCategory[0].name}, so they can be addressed together.`]);
  }
  if (passed) {
    takeaways.push([`${passed} control${passed === 1 ? '' : 's'} confirmed in place`,
      'These checks passed and are listed in the What\'s working section.']);
  }

  const summaryPage = `
    ${eyebrow('01 · Executive summary')}
    ${heading(criticalCount
      ? `${bandLabel} risk, with critical exposure`
      : highCount
        ? `${bandLabel} risk, with no critical exposure`
        : `${bandLabel} risk across ${total} checks`)}
    ${lede(`${esc(displayDomain)} scored <strong>${score} out of 100</strong>, on a scale where 100 is the best
      possible result and 0 is the worst. ${criticalCount ? `${criticalCount} critical and ` : 'No critical vulnerabilities were detected. '}
      ${highCount
        ? `${highCount} high-severity ${highCount === 1 ? 'finding' : 'findings'} carry the most weight in this score.`
        : issueCount
          ? `The ${plural(issueCount, 'remaining issue')} ${issueCount === 1 ? 'is a hardening gap' : 'are hardening gaps'} rather than active exposure.`
          : 'Every check passed.'}`)}

    <div style="margin-top:30px;display:flex;gap:20px;">
      ${stat(`${score}`, 'Security score', P.accent)}
      ${stat(passed, 'Checks passed', P.accent)}
      ${stat(issueCount, 'Issues found', P.accent)}
      ${stat(criticalCount, 'Critical vulnerabilities', P.accent)}
    </div>

    <div style="margin-top:34px;">
      <div style="font-family:${FONT_BODY};font-size:13px;font-weight:700;color:${P.ink};margin-bottom:10px;">Severity distribution</div>
      <div style="display:flex;height:24px;background:${P.accentSoft};">
        ${issueCount ? `
        ${segment(criticalCount, P.critical, 'Critical', '#fff')}
        ${segment(highCount, P.high, 'High', '#fff')}
        ${segment(mediumCount, P.medium, 'Medium', P.ink)}
        ${segment(lowCount, P.low, 'Low', P.ink)}` : `
        <div style="width:100%;background:${P.pass};color:#fff;font-family:${FONT_MONO};font-size:10px;
                    line-height:24px;padding-left:8px;box-sizing:border-box;">No issues · all ${plural(total, 'check')} passed</div>`}
      </div>
      <div style="margin-top:10px;display:flex;gap:18px;flex-wrap:wrap;">
        ${legendDot(P.critical, 'Critical', criticalCount)}
        ${legendDot(P.high, 'High', highCount)}
        ${legendDot(P.medium, 'Medium', mediumCount)}
        ${legendDot(P.low, 'Low', lowCount)}
      </div>
    </div>

    <div style="margin-top:34px;">
      <div style="font-family:${FONT_BODY};font-size:13px;font-weight:700;color:${P.ink};margin-bottom:4px;">Key takeaways</div>
      <div style="display:flex;flex-wrap:wrap;">
        ${takeaways.slice(0, 4).map(([t, d], i) => `
          <div style="width:50%;box-sizing:border-box;padding:16px ${i % 2 === 0 ? '24px' : '0'} 0 0;">
            <div style="height:1px;background:${P.rule};margin-bottom:12px;"></div>
            <div style="font-family:${FONT_MONO};font-size:10px;color:${P.accent};letter-spacing:.1em;">${'ABCD'[i]}</div>
            <div style="margin-top:6px;font-family:${FONT_BODY};font-size:13.5px;font-weight:700;color:${P.ink};">${esc(t)}</div>
            <div style="margin-top:6px;font-family:${FONT_BODY};font-size:12.5px;line-height:1.55;color:${P.body};">${esc(d)}</div>
          </div>`).join('')}
      </div>
    </div>`;

  // ── 3. Findings at a glance ─────────────────────────────────────────────────────────
  const countIn = (cat, sev) => (cat.findings || []).filter((f) => !isPass(f) && (
    sev === 'low' ? (f.severity === 'low' || f.severity === 'info') : f.severity === sev
  )).length;
  const passedIn = (cat) => (cat.findings || []).filter(isPass).length;

  const numCell = (n) => `<td style="text-align:right;padding:14px 0;font-family:${FONT_BODY};font-size:14px;
    color:${n ? P.ink : P.muted};font-weight:${n ? 700 : 400};font-variant-numeric:tabular-nums;">${n}</td>`;

  const glancePage = `
    ${eyebrow('02 · Findings at a glance')}
    ${heading('Findings by security domain')}
    <p style="margin:10px 0 0;font-family:${FONT_BODY};font-size:13px;color:${P.muted};">
      ${plural(categories.length, 'category', 'categories')} · ${plural(total, 'check')} · ${plural(issueCount, 'issue')}
    </p>

    <table style="width:100%;border-collapse:collapse;margin-top:26px;">
      <thead>
        <tr style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:${P.muted};">
          <th style="text-align:left;padding-bottom:10px;font-weight:400;">Category</th>
          <th style="text-align:right;padding-bottom:10px;font-weight:400;width:62px;">Crit</th>
          <th style="text-align:right;padding-bottom:10px;font-weight:400;width:62px;">High</th>
          <th style="text-align:right;padding-bottom:10px;font-weight:400;width:62px;">Med</th>
          <th style="text-align:right;padding-bottom:10px;font-weight:400;width:62px;">Low</th>
          <th style="text-align:right;padding-bottom:10px;font-weight:400;width:62px;">Passed</th>
        </tr>
      </thead>
      <tbody>
        ${categories.map((c) => `
          <tr style="border-top:1px solid ${P.ruleStrong};">
            <td style="padding:14px 16px 14px 0;">
              <div style="font-family:${FONT_BODY};font-size:13.5px;font-weight:700;color:${P.ink};">${esc(c.name)}</div>
              ${c.description ? `<div style="margin-top:3px;font-family:${FONT_BODY};font-size:11.5px;line-height:1.45;color:${P.muted};">${esc(clean(c.description, 90))}</div>` : ''}
            </td>
            ${numCell(countIn(c, 'critical'))}
            ${numCell(countIn(c, 'high'))}
            ${numCell(countIn(c, 'medium'))}
            ${numCell(countIn(c, 'low'))}
            ${numCell(passedIn(c))}
          </tr>`).join('')}
        <tr style="border-top:2px solid ${P.ruleStrong};">
          <td style="padding:14px 0;font-family:${FONT_BODY};font-size:13.5px;font-weight:700;color:${P.ink};">All categories</td>
          ${numCell(criticalCount)}${numCell(highCount)}${numCell(mediumCount)}${numCell(lowCount)}${numCell(passed)}
        </tr>
      </tbody>
    </table>

    <div style="margin-top:38px;display:flex;gap:30px;">
      <div style="flex:1;">
        ${eyebrow('How to read severity', P.muted)}
        <div style="margin-top:12px;">
          ${[['Critical', P.critical, 'Exposure that could be exploited immediately. Act now.'],
             ['High', P.high, 'A meaningful weakness. Address first.'],
             ['Medium', P.medium, 'A hardening gap that raises risk. Schedule a fix.'],
             ['Low', P.low, 'A minor or informational item. Review when convenient.']]
            .map(([l, c, d]) => `
              <div style="display:flex;gap:10px;margin-bottom:10px;">
                <span style="width:10px;height:10px;background:${c};flex:none;margin-top:4px;"></span>
                <div style="font-family:${FONT_BODY};font-size:12px;line-height:1.5;color:${P.body};">
                  <strong style="color:${P.ink};">${l}</strong> · ${d}
                </div>
              </div>`).join('')}
        </div>
      </div>
      <div style="flex:1;">
        ${eyebrow('How to read the score', P.muted)}
        <p style="margin:12px 0 0;font-family:${FONT_BODY};font-size:12px;line-height:1.6;color:${P.body};">
          Scores run from 0 to 100, and higher is better: 100 means no issues were found, 0 indicates
          critical risk. ${esc(displayDomain)} scored <strong>${score}</strong>, rated <strong>${bandLabel}</strong> risk.
        </p>
        <p style="margin:10px 0 0;font-family:${FONT_BODY};font-size:12px;line-height:1.6;color:${P.body};">
          Passing checks raise the score, and the most severe finding caps it — so a single critical
          issue cannot be offset by a long list of checks that passed.
        </p>
      </div>
    </div>`;

  // ── 4. Priority findings ────────────────────────────────────────────────────────────
  const findingCard = (f) => `
    <div style="border:1px solid ${P.rule};background:${P.surface};padding:22px 24px;margin-bottom:18px;">
      <div style="display:flex;align-items:center;gap:10px;">
        ${sevPill(f.severity)}
        ${f.impact ? outlinePill(`Impact ${f.impact} pts`) : ''}
        <span style="margin-left:auto;font-family:${FONT_MONO};font-size:10px;letter-spacing:.08em;
                     text-transform:uppercase;color:${P.muted};">${esc(f.category || '')}</span>
      </div>
      <div style="margin-top:14px;font-family:${FONT_DISPLAY};font-size:22px;line-height:1.2;color:${P.ink};">${esc(f.title)}</div>
      ${f.description ? `<div style="margin-top:8px;font-family:${FONT_BODY};font-size:12.5px;line-height:1.55;color:${P.body};">${esc(clean(f.description, 150))}</div>` : ''}
      <div style="height:1px;background:${P.rule};margin:16px 0;"></div>
      <div style="display:flex;gap:26px;">
        <div style="flex:1;">
          ${eyebrow('What it means', P.muted)}
          <div style="margin-top:8px;font-family:${FONT_BODY};font-size:12.5px;line-height:1.55;color:${P.body};">${esc(clean(whyFor(f), 240))}</div>
        </div>
        <div style="flex:1;">
          ${eyebrow('Recommended action', P.muted)}
          <div style="margin-top:8px;font-family:${FONT_BODY};font-size:12.5px;line-height:1.55;color:${P.body};">${esc(clean(fixFor(f), 240))}</div>
        </div>
      </div>
    </div>`;

  // Two cards is what fits under a section heading; three overflow the band.
  const priorityPages = [];
  for (let i = 0; i < highFindings.length; i += 2) {
    const slice = highFindings.slice(i, i + 2);
    priorityPages.push(`
      ${eyebrow(`${sectionNo.high} · Priority findings${highFindings.length > 2 ? ` · ${Math.floor(i / 2) + 1} of ${Math.ceil(highFindings.length / 2)}` : ''}`)}
      ${heading(i === 0
        ? (highFindings.length === 1 ? 'Address first: one priority finding' : `Address first: ${highFindings.length} priority findings`)
        : 'Priority findings, continued')}
      ${i === 0 ? lede('These carry the highest impact rating in the assessment. Each is shown with what it means in practice and the action that closes it.') : ''}
      <div style="margin-top:24px;">${slice.map(findingCard).join('')}</div>`);
  }

  // ── 5. Medium severity ──────────────────────────────────────────────────────────────
  const mediumRow = (f) => `
    <tr style="border-top:1px solid ${P.rule};">
      <td style="padding:16px 18px 16px 0;vertical-align:top;width:31%;">
        <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;">
          ${sevPill('medium')}${f.impact ? outlinePill(`${f.impact} pts`) : ''}
        </div>
        <div style="font-family:${FONT_BODY};font-size:13px;font-weight:700;color:${P.ink};">${esc(f.title)}</div>
        <div style="margin-top:4px;font-family:${FONT_BODY};font-size:11.5px;line-height:1.45;color:${P.muted};">${esc(f.category || '')}</div>
      </td>
      <td style="padding:16px 18px 16px 0;vertical-align:top;font-family:${FONT_BODY};font-size:12px;line-height:1.55;color:${P.body};">
        ${esc(clean(whyFor(f), 170))}
      </td>
      <td style="padding:16px 0;vertical-align:top;font-family:${FONT_BODY};font-size:12px;line-height:1.55;color:${P.body};">
        ${esc(clean(fixFor(f), 170))}
      </td>
    </tr>`;

  const mediumPages = [];
  for (let i = 0; i < mediumFindings.length; i += 4) {
    const slice = mediumFindings.slice(i, i + 4);
    mediumPages.push(`
      ${eyebrow(`${sectionNo.medium} · Priority findings · Medium severity`)}
      ${heading(i === 0
        ? `${mediumFindings.length} medium-severity hardening ${mediumFindings.length === 1 ? 'gap' : 'gaps'}`
        : 'Medium severity, continued')}
      ${i === 0 ? lede('Each raises risk without being exploitable on its own. They are usually quick to close alongside the priority findings above.') : ''}
      <table style="width:100%;border-collapse:collapse;margin-top:24px;">
        <thead>
          <tr style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:${P.muted};">
            <th style="text-align:left;padding-bottom:10px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Finding</th>
            <th style="text-align:left;padding-bottom:10px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">What it means</th>
            <th style="text-align:left;padding-bottom:10px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Recommended action</th>
          </tr>
        </thead>
        <tbody>${slice.map(mediumRow).join('')}</tbody>
      </table>`);
  }

  // ── 6. Low severity and informational ───────────────────────────────────────────────
  const statusFor = (f) => {
    if (f.status === 'pass') return ['In place', P.pass];
    if (f.severity === 'info') return ['Info', P.accent];
    return ['Review', P.medium];
  };

  const lowRow = (f) => {
    const [label, color] = statusFor(f);
    return `
      <tr style="border-top:1px solid ${P.rule};">
        <td style="padding:11px 16px 11px 0;vertical-align:top;">
          <div style="font-family:${FONT_BODY};font-size:12.5px;font-weight:700;color:${P.ink};">${esc(f.title)}</div>
          ${f.description ? `<div style="margin-top:2px;font-family:${FONT_BODY};font-size:11.5px;line-height:1.45;color:${P.muted};">${esc(clean(f.description, 95))}</div>` : ''}
        </td>
        <td style="padding:11px 16px 11px 0;vertical-align:top;font-family:${FONT_BODY};font-size:11.5px;color:${P.body};width:30%;">${esc(f.category || '')}</td>
        <td style="padding:11px 0;vertical-align:top;width:84px;">
          <span style="display:inline-block;padding:3px 8px;border-radius:2px;background:${color};color:#fff;
                       font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.06em;">${esc(label)}</span>
        </td>
      </tr>`;
  };

  const lowPages = [];
  for (let i = 0; i < lowFindings.length; i += 12) {
    const slice = lowFindings.slice(i, i + 12);
    lowPages.push(`
      ${eyebrow(`${sectionNo.low} · Low severity & informational`)}
      ${heading(i === 0
        ? `${lowFindings.length} low-severity observation${lowFindings.length === 1 ? '' : 's'}`
        : 'Low severity, continued')}
      ${i === 0 ? lede('Minor items that record configuration for reference or merit periodic review.') : ''}
      <table style="width:100%;border-collapse:collapse;margin-top:22px;">
        <thead>
          <tr style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:${P.muted};">
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Finding</th>
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Category</th>
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Status</th>
          </tr>
        </thead>
        <tbody>${slice.map(lowRow).join('')}</tbody>
      </table>`);
  }

  // ── 7. What's working ───────────────────────────────────────────────────────────────
  // Passed checks have their own section, after the issues — the same order as the
  // on-screen report. They used to be folded into the low-severity list as if they were
  // observations to review.
  const passPages = [];
  for (let i = 0; i < passes.length; i += 12) {
    const slice = passes.slice(i, i + 12);
    passPages.push(`
      ${eyebrow(`${sectionNo.pass} · What's working`)}
      ${heading(i === 0
        ? `${plural(passes.length, 'control')} confirmed in place`
        : "What's working, continued")}
      ${i === 0 ? lede('Each of these checks passed. Together they are what keeps the score where it is.') : ''}
      <table style="width:100%;border-collapse:collapse;margin-top:22px;">
        <thead>
          <tr style="font-family:${FONT_MONO};font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:${P.muted};">
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Check</th>
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Category</th>
            <th style="text-align:left;padding-bottom:9px;font-weight:400;border-bottom:2px solid ${P.ruleStrong};">Status</th>
          </tr>
        </thead>
        <tbody>${slice.map(lowRow).join('')}</tbody>
      </table>`);
  }

  // ── Assemble ────────────────────────────────────────────────────────────────────────
  const bodies = [
    coverPage,
    summaryPage,
    glancePage,
    ...priorityPages,
    ...mediumPages,
    ...lowPages,
    ...passPages,
  ];
  const runningHead = `${displayDomain} · ${magnet.reportTitle}`;
  const pages = bodies.map((inner, i) => page(inner, {
    runningHead: i === 0 ? (prospectCompany ? 'Prospect security report' : 'Security report') : runningHead,
    pageNo: i + 1,
    pageCount: bodies.length,
  }));

  // ── Rasterize ───────────────────────────────────────────────────────────────────────
  const host = document.createElement('div');
  host.style.cssText = `position:absolute;left:-10000px;top:0;width:${PAGE_W}px;background:${P.ground};`;
  document.body.appendChild(host);

  const canvases = [];
  try {
    for (let i = 0; i < pages.length; i++) {
      host.innerHTML = pages[i];
      // eslint-disable-next-line no-await-in-loop
      const canvas = await window.html2canvas(host.firstElementChild, {
        scale: 2,
        useCORS: true,
        backgroundColor: P.ground,
        logging: false,
        width: PAGE_W,
        height: PAGE_H,
        windowWidth: PAGE_W,
      });
      canvases.push(canvas);
      progress(15 + ((i + 1) / pages.length) * 70, `Rendering page ${i + 1} of ${pages.length}…`);
    }
  } finally {
    document.body.removeChild(host);
  }

  progress(90, 'Creating PDF…');
  const { jsPDF } = window.jspdf;
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  canvases.forEach((canvas, i) => {
    if (i > 0) pdf.addPage();
    // Full-bleed: the page design already carries its own margins, head rule and footer.
    pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
  });

  progress(100, 'Done');

  const name = fileName
    || `${(prospectCompany || displayDomain).replace(/[^a-z0-9]+/gi, '-')}-${magnet.reportTitle.replace(/[^a-z0-9]+/gi, '-')}.pdf`;

  if (saveFile) { pdf.save(name); return undefined; }
  return pdf.output('blob');
}

export default generateEditorialPDF;
